import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { sideBarItems } from '../../utils';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SubscriptionService from '../../services/admin/SubscriptionService';

const initialForm = {
  name: '',
  durationDays: '',
  price: '',
  isActive: true
};

function SubscriptionPacks() {
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  useEffect(() => {
    loadPacks();
  }, []);

  const getError = (err) => err?.response?.data?.error || 'Operation failed';

  const loadPacks = async () => {
    try {
      const res = await SubscriptionService.getAllPacks();
      const list = res?.data?.packs || res?.data?.data || res?.data || [];
      setPacks(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const validateForm = () => {
    if (!form.name?.trim()) {
      toast.error('Pack name is required');
      return false;
    }

    const durationDays = Number(form.durationDays);
    const price = Number(form.price);

    if (Number.isNaN(durationDays) || durationDays <= 0) {
      toast.error('durationDays must be greater than 0');
      return false;
    }

    if (Number.isNaN(price) || price < 0) {
      toast.error('price must be 0 or greater');
      return false;
    }

    return true;
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const submit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        durationDays: Number(form.durationDays),
        price: Number(form.price),
        isActive: !!form.isActive
      };

      if (editingId) {
        await SubscriptionService.updatePack(editingId, payload);
        toast.success('Pack updated successfully');
      } else {
        await SubscriptionService.createPack(payload);
        toast.success('Pack created successfully');
      }

      resetForm();
      await loadPacks();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (pack) => {
    setEditingId(pack._id);
    setForm({
      name: pack.name || '',
      durationDays: pack.durationDays || '',
      price: pack.price || '',
      isActive: !!pack.isActive
    });
  };

  const confirmDeletePack = async () => {
    if (!confirmDelete.id) {
      return;
    }

    setLoading(true);
    try {
      await SubscriptionService.deletePack(confirmDelete.id);
      toast.success('Pack deleted successfully');
      setConfirmDelete({ open: false, id: null });
      await loadPacks();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const togglePackStatus = async (pack) => {
    setLoading(true);
    try {
      await SubscriptionService.updatePack(pack._id, { isActive: !pack.isActive });
      toast.success(`Pack ${!pack.isActive ? 'activated' : 'deactivated'} successfully`);
      await loadPacks();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title={sideBarItems.subscriptionPacks.key}>
      <div className="w-full py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Subscription Packs</h1>

        <div className="bg-white shadow-md rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">{editingId ? 'Update Pack' : 'Create Pack'}</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Pack name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <input
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Duration days"
              type="number"
              value={form.durationDays}
              onChange={(e) => handleChange('durationDays', e.target.value)}
            />
            <input
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Price"
              type="number"
              value={form.price}
              onChange={(e) => handleChange('price', e.target.value)}
            />
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={form.isActive ? 'true' : 'false'}
              onChange={(e) => handleChange('isActive', e.target.value === 'true')}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={submit}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {editingId ? 'Update' : 'Create'}
            </button>
            {editingId && (
              <button
                onClick={resetForm}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Pack List</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-4 py-2 border">Name</th>
                  <th className="px-4 py-2 border">Duration</th>
                  <th className="px-4 py-2 border">Price</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Toggle</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((pack) => (
                  <tr key={pack._id} className="text-sm">
                    <td className="px-4 py-2 border">{pack.name}</td>
                    <td className="px-4 py-2 border">{pack.durationDays} days</td>
                    <td className="px-4 py-2 border">৳{pack.price}</td>
                    <td className="px-4 py-2 border">{pack.isActive ? 'Active' : 'Inactive'}</td>
                    <td className="px-4 py-2 border">
                      <button
                        onClick={() => togglePackStatus(pack)}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pack.isActive ? 'bg-indigo-600' : 'bg-gray-300'} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        type="button"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${pack.isActive ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2 border">
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(pack)}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setConfirmDelete({ open: true, id: pack._id })}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-md"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!packs.length && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No packs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: null })}
        onConfirm={confirmDeletePack}
        title="Delete Subscription Pack"
        message="Are you sure you want to delete this pack?"
      />
    </Layout>
  );
}

export default SubscriptionPacks;
